import axios from 'axios';
import {useEffect, useRef, useState} from 'react';
import {getBerlinDate} from '../utils/berlinDate';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const entries = new Map();
const listeners = new Set();
const EMPTY = Object.freeze({data: null, error: null, loading: false, updatedAt: 0});
export const ORDER_RESOURCES = ['config', 'rolls', 'toppings', 'extras', 'categories', 'lunch', 'dinner', 'lunchVariants', 'dinnerVariants'];
const dailyResource = kind => ['lunch', 'dinner', 'lunchVariants', 'dinnerVariants'].includes(kind);
const keyFor = (department, kind, day) => JSON.stringify([department, kind, dailyResource(kind) ? day : '']);
const urlsFor = (department, kind, day) => {
  const id = encodeURIComponent(department);
  return {
    config: [`/department-config/${id}`],
    rolls: [`/menu/breakfast/${id}`], toppings: [`/menu/toppings/${id}`], extras: [`/department-settings/${id}`],
    categories: [`/menu/categories/${id}`],
    lunch: [`/daily-lunch-price/${id}/${day}`], dinner: [`/daily-dinner-price/${id}/${day}`],
    lunchVariants: [`/daily-meal-variants/${id}/${day}/lunch`], dinnerVariants: [`/daily-meal-variants/${id}/${day}/dinner`],
  }[kind];
};
const emit = () => listeners.forEach(listener => listener());
export const clearOrderMenuCache = () => { entries.clear(); emit(); };
export const readOrderResource = (department, kind, day) => entries.get(keyFor(department, kind, day))?.snapshot || EMPTY;
export const finitePrice = value => typeof value === 'number' && Number.isFinite(value);
export const validPrice = value => finitePrice(value) && value >= 0;

function validateResource(kind, values) {
  let valid = true;
  if (kind === 'config') valid = typeof values[0]?.is_breakfast_lunch_enabled === 'boolean';
  // Validate transport shape here; individual prices are checked per offered item.
  if (['rolls', 'toppings', 'categories'].includes(kind)) valid = Array.isArray(values[0]) && values[0].every(item => item && typeof item === 'object');
  if (kind === 'extras') valid = values[0] && typeof values[0] === 'object' && !Array.isArray(values[0]);
  if (['lunch', 'dinner'].includes(kind)) valid = validPrice(values[0]?.[`${kind}_price`]);
  if (kind.endsWith('Variants')) valid = Array.isArray(values[0]?.variants) && values[0].variants.every(v => v && validPrice(v.price));
  if (!valid) throw Object.assign(new Error('Preisdaten fehlen oder sind ungültig. Bitte den Admin unter Menü & Preise prüfen lassen.'), {priceDataError: true});
}

export function priceLoadReason(error) {
  if (error?.priceDataError) return error.message;
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return 'Der Server hat nicht innerhalb von 10 Sekunden geantwortet. Bitte erneut laden.';
  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'Die Anmeldung ist abgelaufen oder die Berechtigung fehlt. Bitte erneut anmelden.';
  if (status === 404) return 'Die angeforderten Daten wurden nicht gefunden. Bitte den Admin informieren (Fehler 404).';
  if (status) return `Der Server konnte die Preise nicht liefern (Fehler ${status}). Bitte erneut laden oder den Admin informieren.`;
  return 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen und erneut laden.';
}

export function loadOrderResource(department, kind, day = getBerlinDate(), force = false) {
  const key = keyFor(department, kind, day);
  let entry = entries.get(key);
  if (!entry) { entry = {snapshot: EMPTY}; entries.set(key, entry); }
  if (entry.pending) return entry.pending;
  // Categories contain live stock. Keep their last display value, but never
  // reuse it instead of a requested server refresh (including reopening).
  const ttl = kind === 'categories' ? 0 : dailyResource(kind) ? 30000 : 300000;
  if (!force && entry.snapshot.data && !entry.snapshot.error && Date.now() - entry.snapshot.updatedAt < ttl) {
    return Promise.resolve(entry.snapshot.data);
  }
  entry.snapshot = {...entry.snapshot, loading: true};
  entry.pending = Promise.all(urlsFor(department, kind, day).map(url => axios.get(`${API}${url}`, {timeout: 10000})))
    .then(responses => {
      const values = responses.map(response => response.data);
      validateResource(kind, values);
      let data = values[0];
      // Preserve references on unchanged refreshes so form effects do not reset selections.
      if (JSON.stringify(data) === JSON.stringify(entry.snapshot.data)) data = entry.snapshot.data;
      entry.snapshot = {data, error: null, loading: false, updatedAt: Date.now()};
      return data;
    }).catch(error => {
      entry.snapshot = {...entry.snapshot, loading: false, error};
      throw error;
    }).finally(() => { entry.pending = null; emit(); });
  emit();
  return entry.pending;
}

export const preloadOrderMenu = department => Promise.allSettled(
  ORDER_RESOURCES.map(kind => loadOrderResource(department, kind, getBerlinDate()))
);

export function useOrderMenuData(department) {
  const [, render] = useState(0);
  const previousData = useRef({});
  const day = getBerlinDate();
  useEffect(() => {
    const listener = () => render(value => value + 1);
    listeners.add(listener);
    const refresh = () => { if (department) preloadOrderMenu(department); };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    const stockTimer = window.setInterval(() => {
      if (department && document.visibilityState !== 'hidden') {
        loadOrderResource(department, 'categories').catch(() => {});
      }
    }, 5000);
    window.addEventListener('focus', refresh);
    return () => { listeners.delete(listener); window.clearInterval(timer); window.clearInterval(stockTimer); window.removeEventListener('focus', refresh); };
  }, [department]);
  const resources = Object.fromEntries(ORDER_RESOURCES.map(kind => [kind, readOrderResource(department, kind, day)]));
  if (previousData.current.department !== department) previousData.current = {department};
  const display = {};
  for (const kind of ORDER_RESOURCES) {
    if (resources[kind].data) previousData.current[kind] = resources[kind].data;
    // Display the last known data without treating it as a verified price for today.
    display[kind] = resources[kind].data || previousData.current[kind] || null;
  }
  const refresh = kinds => Promise.all(kinds.map(kind => loadOrderResource(department, kind, getBerlinDate(), true)));
  return {...resources, display, day, refresh};
}

const isOrderWrite = config => {
  const method = config?.method?.toLowerCase();
  return ['post', 'put', 'patch', 'delete'].includes(method)
    && /\/orders(?:\/|\?|$)/.test(config?.url || '');
};

async function refreshStockAfterOrder() {
  await Promise.allSettled([...entries].filter(([key]) => JSON.parse(key)[1] === 'categories').map(async ([key, entry]) => {
    entry.snapshot = {...entry.snapshot, updatedAt: 0};
    // An in-flight GET may still contain the stock from BEFORE the mutation.
    // Let it finish, then request a fresh snapshot rather than deduplicating it.
    if (entry.pending) await entry.pending.catch(() => {});
    if (entries.get(key) !== entry) return;
    const [department, kind, day] = JSON.parse(key);
    await loadOrderResource(department, kind, day, true);
  }));
}

// Successful stock mutations refresh before the caller displays success.
// A failed GET must never cause an already saved order to be submitted again.
export function installOrderMenuInvalidation(client) {
  return client.interceptors.response.use(response => {
    if (isOrderWrite(response.config)) {
      return refreshStockAfterOrder().then(() => response);
    }
    const method = response.config?.method?.toLowerCase();
    const url = response.config?.url || '';
    if (method && !['get', 'head', 'options'].includes(method) && /menu|department-config|department-settings|daily-(lunch|dinner)-(price|settings)|meal-variants|lunch-settings/.test(url)) {
      // Keep displayed data and drafts while fetching the admin's changes.
      for (const [key, entry] of entries) {
        entry.snapshot = {...entry.snapshot, updatedAt: 0};
        const [department, kind, day] = JSON.parse(key);
        const refresh = () => {
          if (entries.get(key) === entry) loadOrderResource(department, kind, day, true).catch(() => {});
        };
        if (entry.pending) entry.pending.then(refresh, refresh);
        else refresh();
      }
    }
    return response;
  }, async error => {
    if (isOrderWrite(error.config) && error.response?.status === 409) {
      await refreshStockAfterOrder();
    }
    throw error;
  });
}
